import {
  IManagedDbBatchRequest,
  IManagedDbBatchResponse,
  IManagedDbBatchResult,
  IManagedDbRowKeyEntry,
  IManagedDbRowMutation
} from "../../services/table-editor-service";

export type TableEditorAlertTone = "info" | "success" | "warn" | "error";
export type TableEditorErrorKind =
  | "network"
  | "timeout"
  | "authorization"
  | "permission"
  | "notFound"
  | "validation"
  | "conflict"
  | "server"
  | "unknown";
export type TableEditorOperation = "load" | "save" | "export" | "import" | "ai" | "sqlPreview" | "notice";

export interface ITableEditorAlert {
  tone: TableEditorAlertTone;
  operation: TableEditorOperation;
  title: string;
  message: string;
  details: string[];
  nextSteps: string[];
  kind?: TableEditorErrorKind;
  status?: number;
  technicalDetails?: string;
}

export interface ICreateTableEditorAlertOptions {
  tone: TableEditorAlertTone;
  operation: TableEditorOperation;
  title: string;
  message: string;
  details?: string[];
  nextSteps?: string[];
  kind?: TableEditorErrorKind;
  status?: number;
  technicalDetails?: string;
}

export interface IBuildTableEditorErrorAlertOptions {
  operation: TableEditorOperation;
  title: string;
  fallbackMessage: string;
}

interface IApiErrorPayload {
  message?: unknown;
  Message?: unknown;
  error?: unknown;
  title?: unknown;
  detail?: unknown;
  errors?: unknown;
}

interface IErrorResponseShape {
  status?: unknown;
  data?: unknown;
}

interface IErrorShape {
  code?: unknown;
  message?: unknown;
  response?: IErrorResponseShape;
}

interface IExtractedError {
  code?: string;
  rawMessage?: string;
  responseData?: unknown;
  status?: number;
}

const MAX_SERVER_ERROR_BODY_CHARS = 12 * 1024;
const MAX_DETAIL_ROWS = 12;

export const createTableEditorAlert = (options: ICreateTableEditorAlertOptions): ITableEditorAlert => {
  return {
    tone: options.tone,
    operation: options.operation,
    title: options.title,
    message: options.message,
    details: options.details ?? [],
    nextSteps: options.nextSteps ?? [],
    kind: options.kind,
    status: options.status,
    technicalDetails: options.technicalDetails
  };
};

const isPlainObject = (value: unknown): value is object => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const stringFromUnknown = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed: string = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
};

const collectStringErrors = (value: unknown): string[] => {
  const result: string[] = [];
  if (Array.isArray(value)) {
    value.forEach((item: unknown): void => {
      const text: string | null = stringFromUnknown(item);
      if (text !== null) {
        result.push(text);
        return;
      }
      collectStringErrors(item).forEach((nested: string): void => {
        result.push(nested);
      });
    });
    return result;
  }
  if (isPlainObject(value)) {
    const values: unknown[] = Object.values(value);
    values.forEach((item: unknown): void => {
      collectStringErrors(item).forEach((nested: string): void => {
        result.push(nested);
      });
    });
  }
  return result;
};

const textFromPayload = (payload: unknown): string | null => {
  const direct: string | null = stringFromUnknown(payload);
  if (direct !== null) {
    return direct;
  }
  if (!isPlainObject(payload)) {
    return null;
  }
  const apiPayload: Partial<IApiErrorPayload> = payload as Partial<IApiErrorPayload>;
  const candidates: unknown[] = [
    apiPayload.message,
    apiPayload.Message,
    apiPayload.error,
    apiPayload.detail,
    apiPayload.title
  ];
  for (const candidate of candidates) {
    const text: string | null = stringFromUnknown(candidate);
    if (text !== null) {
      return text;
    }
  }
  const errorDetails: string[] = collectStringErrors(apiPayload.errors);
  if (errorDetails.length > 0) {
    return errorDetails[0]!;
  }
  return null;
};

const detailsFromPayload = (payload: unknown): string[] => {
  if (!isPlainObject(payload)) {
    return [];
  }
  const apiPayload: Partial<IApiErrorPayload> = payload as Partial<IApiErrorPayload>;
  return collectStringErrors(apiPayload.errors).slice(0, MAX_DETAIL_ROWS);
};

const readBlobPayload = async (payload: Blob): Promise<string | null> => {
  if (payload.size === 0 || payload.size > MAX_SERVER_ERROR_BODY_CHARS) {
    return null;
  }
  try {
    const text: string = await payload.text();
    const trimmed: string = text.trim();
    if (trimmed.length === 0) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return textFromPayload(parsed) ?? trimmed;
    } catch {
      return trimmed;
    }
  } catch {
    return null;
  }
};

const extractError = (error: unknown): IExtractedError => {
  if (!isPlainObject(error) && !(error instanceof Error)) {
    const raw: string | null = stringFromUnknown(error);
    return raw === null ? {} : { rawMessage: raw };
  }
  const shape: Partial<IErrorShape> = error as Partial<IErrorShape>;
  const status: number | undefined = typeof shape.response?.status === "number" ? shape.response.status : undefined;
  const code: string | undefined = typeof shape.code === "string" ? shape.code : undefined;
  let rawMessage: string | undefined;
  if (typeof shape.message === "string" && shape.message.trim().length > 0) {
    rawMessage = shape.message.trim();
  } else if (error instanceof Error && error.message.trim().length > 0) {
    rawMessage = error.message.trim();
  }
  return {
    status,
    code,
    rawMessage,
    responseData: shape.response?.data
  };
};

const kindFromError = (extracted: IExtractedError): TableEditorErrorKind => {
  if (extracted.code === "ECONNABORTED" || extracted.code === "ETIMEDOUT") {
    return "timeout";
  }
  if (extracted.status === undefined) {
    if (extracted.code === "ERR_NETWORK") {
      return "network";
    }
    if (
      extracted.rawMessage === "Network Error" ||
      extracted.rawMessage === "Failed to fetch" ||
      extracted.rawMessage === "Load failed"
    ) {
      return "network";
    }
    return "unknown";
  }
  if (extracted.status === 401) {
    return "authorization";
  }
  if (extracted.status === 403) {
    return "permission";
  }
  if (extracted.status === 404) {
    return "notFound";
  }
  if (extracted.status === 409) {
    return "conflict";
  }
  if (extracted.status === 400 || extracted.status === 422) {
    return "validation";
  }
  if (extracted.status >= 500) {
    return "server";
  }
  return "unknown";
};

const messageForKind = (kind: TableEditorErrorKind, fallbackMessage: string): string => {
  if (kind === "network") {
    return "Couldn't connect.";
  }
  if (kind === "timeout") {
    return "The request took too long to complete.";
  }
  if (kind === "authorization") {
    return "Your session is no longer signed in.";
  }
  if (kind === "permission") {
    return "You do not have permission to manage this table's data.";
  }
  if (kind === "notFound") {
    return "This table could not be found in the current project.";
  }
  if (kind === "conflict") {
    return "The data was changed elsewhere before this operation completed.";
  }
  if (kind === "server") {
    return "Something went wrong while processing this request.";
  }
  return fallbackMessage;
};

const nextStepsForKind = (kind: TableEditorErrorKind, operation: TableEditorOperation): string[] => {
  if (kind === "network") {
    return ["Check your connection, then retry.", "If the issue persists, try again in a moment."];
  }
  if (kind === "timeout") {
    return ["Retry the operation.", "Narrow filters or reduce import/export size if this repeats."];
  }
  if (kind === "authorization") {
    return ["Refresh the page and sign in again if needed."];
  }
  if (kind === "permission") {
    return ["Ask a project admin for the Manage Data Table permission."];
  }
  if (kind === "notFound") {
    return ["Refresh the datasource tree.", "Verify the table still exists in the current project schema."];
  }
  if (kind === "conflict") {
    return ["Reload rows to see the current server values.", "Reapply the intended edit and save again."];
  }
  if (kind === "validation" && operation === "save") {
    return [
      "Open Preview SQL to inspect the pending mutations.",
      "Fix the highlighted rows or discard invalid changes, then save again."
    ];
  }
  if (kind === "validation" && operation === "import") {
    return ["Fix the source file or choose the correct import mode, then import again."];
  }
  if (kind === "validation") {
    return ["Review the request inputs and retry."];
  }
  if (kind === "server") {
    return ["Retry once.", "If it repeats, copy the status and technical detail into a bug ticket."];
  }
  return ["Retry the operation."];
};

const technicalDetails = (extracted: IExtractedError): string | undefined => {
  const parts: string[] = [];
  if (extracted.status !== undefined) {
    parts.push(`HTTP ${extracted.status}`);
  }
  if (extracted.code !== undefined) {
    parts.push(`Code ${extracted.code}`);
  }
  if (extracted.rawMessage !== undefined) {
    parts.push(extracted.rawMessage);
  }
  return parts.length > 0 ? parts.join(" | ") : undefined;
};

export const buildTableEditorErrorAlert = async (
  error: unknown,
  options: IBuildTableEditorErrorAlertOptions
): Promise<ITableEditorAlert> => {
  const extracted: IExtractedError = extractError(error);
  const kind: TableEditorErrorKind = kindFromError(extracted);
  let serverMessage: string | null = null;
  let details: string[] = [];
  const responseData: unknown = extracted.responseData;
  if (typeof Blob !== "undefined" && responseData instanceof Blob) {
    serverMessage = await readBlobPayload(responseData);
  } else {
    serverMessage = textFromPayload(responseData);
    details = detailsFromPayload(responseData);
  }
  let message: string;
  if (serverMessage !== null) {
    message = serverMessage;
  } else if (kind === "unknown" && extracted.rawMessage !== undefined) {
    message = extracted.rawMessage;
  } else {
    message = messageForKind(kind, options.fallbackMessage);
  }
  return createTableEditorAlert({
    tone: "error",
    operation: options.operation,
    title: options.title,
    message,
    details,
    nextSteps: nextStepsForKind(kind, options.operation),
    kind,
    status: extracted.status,
    technicalDetails: technicalDetails(extracted)
  });
};

// Insert mutations use a synthetic `tmp-<uuid>` clientId so the reducer can
// address them before they have a server-side pk; surfacing that string in a
// user-facing error reads as gibberish. Reduce inserts to a 1-based position
// within the batch ("New row 2"). Updates and deletes already carry a pk we
// can render directly.
const insertPositionByClientId = (request: IManagedDbBatchRequest): Map<string, number> => {
  const result: Map<string, number> = new Map<string, number>();
  let index: number = 0;
  request.mutations.forEach((mutation: IManagedDbRowMutation): void => {
    if (mutation.op === "insert") {
      result.set(mutation.clientId, index);
      index += 1;
    }
  });
  return result;
};

const mutationLabel = (mutation: IManagedDbRowMutation | undefined, insertPositions: Map<string, number>): string => {
  if (mutation === undefined) {
    return "Mutation";
  }
  let op: string;
  if (mutation.op === "insert") {
    op = "INSERT";
  } else if (mutation.op === "update") {
    op = "UPDATE";
  } else {
    op = "DELETE";
  }
  if (mutation.key?.keys && mutation.key.keys.length > 0) {
    const keyText: string = mutation.key.keys
      .map((entry: IManagedDbRowKeyEntry): string => `${entry.column}=${String(entry.value)}`)
      .join(", ");
    return `${op} ${keyText}`;
  }
  if (mutation.op === "insert") {
    const position: number | undefined = insertPositions.get(mutation.clientId);
    if (position !== undefined) {
      return `INSERT new row ${position + 1}`;
    }
  }
  return `${op} ${mutation.clientId}`;
};

const failureDetails = (request: IManagedDbBatchRequest, failed: IManagedDbBatchResult[]): string[] => {
  const insertPositions: Map<string, number> = insertPositionByClientId(request);
  const details: string[] = failed.slice(0, MAX_DETAIL_ROWS).map((result: IManagedDbBatchResult): string => {
    const mutation: IManagedDbRowMutation | undefined = request.mutations.find(
      (candidate: IManagedDbRowMutation): boolean => candidate.clientId === result.clientId
    );
    return `${mutationLabel(mutation, insertPositions)}: ${result.error ?? "Unknown failure"}`;
  });
  if (failed.length > MAX_DETAIL_ROWS) {
    details.push(
      `${failed.length - MAX_DETAIL_ROWS} more failed mutation${failed.length - MAX_DETAIL_ROWS === 1 ? "" : "s"}.`
    );
  }
  return details;
};

export const buildTableEditorSaveFailureAlert = (
  request: IManagedDbBatchRequest,
  response: IManagedDbBatchResponse
): ITableEditorAlert | null => {
  if (response.success) {
    return null;
  }
  const results: IManagedDbBatchResult[] = response.results ?? [];
  const failed: IManagedDbBatchResult[] = results.filter((result: IManagedDbBatchResult): boolean => !result.success);
  const succeededCount: number = results.filter((result: IManagedDbBatchResult): boolean => result.success).length;
  const failedCount: number =
    failed.length > 0 ? failed.length : Math.max(1, request.mutations.length - succeededCount);
  const conflictCount: number = failed.filter(
    (result: IManagedDbBatchResult): boolean => result.conflict === true
  ).length;
  let title: string;
  let message: string;
  let kind: TableEditorErrorKind;
  let nextSteps: string[];
  if (response.atomic === true) {
    title = "Atomic save rolled back";
    message = `None of the ${request.mutations.length} pending change${request.mutations.length === 1 ? "" : "s"} was committed.`;
    kind = conflictCount > 0 ? "conflict" : "validation";
    nextSteps = [
      "Open Preview SQL to inspect the batch.",
      "Fix the failing mutation, or turn atomic mode off to save valid rows independently."
    ];
  } else if (conflictCount > 0) {
    title = "Some rows changed on the server";
    message = `${succeededCount} change${succeededCount === 1 ? "" : "s"} saved. ${failedCount} still need attention.`;
    kind = "conflict";
    nextSteps = nextStepsForKind(kind, "save");
  } else {
    title = "Some changes could not be saved";
    message = `${succeededCount} change${succeededCount === 1 ? "" : "s"} saved. ${failedCount} failed validation or database constraints.`;
    kind = "validation";
    nextSteps = nextStepsForKind(kind, "save");
  }
  const details: string[] =
    failed.length > 0
      ? failureDetails(request, failed)
      : ["The server returned an unsuccessful batch response without per-row failure details."];
  return createTableEditorAlert({
    tone: "error",
    operation: "save",
    title,
    message,
    details,
    nextSteps,
    kind
  });
};
